import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { PageHeader } from '../components/PageHeader';
import { orgPath, workspacePath } from '../nav';
import { useSession } from '../session';
import { useToast } from '../toast';

interface ObjectConfig {
  apiName: string;
  readableFields: string[];
  updatableFields: string[];
  creatableFields: string[];
  piiFields: string[];
  piiPatterns: string[];
  readFilter: string | null;
  updateFilter: string | null;
  deletable: boolean;
  deleteFilter: string | null;
  maxRows: number;
  filesReadable: boolean;
  filesWritable: boolean;
  fileMaxBytes: number;
}

interface SkillDetail {
  id: string;
  code: string;
  name: string;
  summary: string;
  prompt: string;
  objects: ObjectConfig[];
  status: 'active' | 'disabled';
  grantCount: number;
  /** The org whose schema this skill is written against. A skill belongs to one. */
  customerOrgId: string;
  orgLabel: string;
}

interface SObjectSummary {
  apiName: string;
  label: string;
  custom: boolean;
  createable: boolean;
  updateable: boolean;
  deletable: boolean;
}

interface FieldSummary {
  apiName: string;
  label: string;
  type: string;
  custom: boolean;
  filterable: boolean;
  createable: boolean;
  updateable: boolean;
  redactable: boolean;
}

interface PreviewResult {
  object: string;
  fields: string[];
  records: Record<string, unknown>[];
  count: number;
  partial: boolean;
  redactions: number;
}

/** One identity's stored intersection of this skill with what that identity can actually reach. */
interface SchemaIdentity {
  identityId: string;
  username: string;
  customerOrgId: string;
  capturedAt: string | null;
  objectCount: number;
  fieldCount: number;
}

interface SchemaStatus {
  objects: number;
  /** Why there is no identity to read as, when grants exist but none can be used. */
  blockedBecause: string[];
  identities: SchemaIdentity[];
  /** Captured, but no live grant points at them any more. */
  orphaned: string[];
}

interface CaptureWarning {
  objectName: string;
  fieldName?: string;
  reason: string;
}

interface RefreshOutcome {
  identityId: string;
  username: string;
  customerOrgId: string;
  ok: boolean;
  objectCount?: number;
  fieldCount?: number;
  warnings?: CaptureWarning[];
  error?: string;
}

interface RefreshResult {
  captured: number;
  failed: number;
  results: RefreshOutcome[];
}

interface Pattern {
  name: string;
  label: string;
  description: string;
}

const MERGE_HINT = '{!$User.Id}, {!$User.Username}, {!$Context.RecordId}, {!$Now}';

const blankObject = (apiName: string): ObjectConfig => ({
  apiName,
  readableFields: [],
  updatableFields: [],
  creatableFields: [],
  piiFields: [],
  piiPatterns: [],
  readFilter: null,
  updateFilter: null,
  deletable: false,
  deleteFilter: null,
  maxRows: 200,
  filesReadable: false,
  filesWritable: false,
  fileMaxBytes: 5_242_880,
});

const has = (list: string[], field: string): boolean => list.some((f) => f.toLowerCase() === field.toLowerCase());

const toggled = (list: string[], field: string): string[] =>
  has(list, field) ? list.filter((f) => f.toLowerCase() !== field.toLowerCase()) : [...list, field];

/**
 * One skill's access configuration.
 *
 * The shape follows what an administrator is actually deciding: pick an object, then go down its fields
 * saying what may be done with each. Selected fields float to the top so that reviewing a configured
 * object does not mean hunting through four hundred rows for the six that are ticked, and the rest stay
 * alphabetical so a field can be found by name.
 *
 * Objects and fields are read from a real org through its Connect session, so the lists are that org's
 * and not a guess. The skill belongs to that org, because the objects and fields it names are that org's
 * schema; the same task wanted elsewhere is a skill on each org.
 */
export function Skill() {
  // orgId is only in the address on the current route. The older /workspaces/:t/skills/:skillId still
  // resolves here, and is corrected once the skill says which org it belongs to (see below).
  const { tenantId, skillId, orgId: routeOrgId } = useParams<{ tenantId: string; skillId: string; orgId?: string }>();
  const { me } = useSession();
  const [skill, setSkill] = useState<SkillDetail | null>(null);
  const [objects, setObjects] = useState<ObjectConfig[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Schema browsing
  const orgId = skill?.customerOrgId ?? '';
  const toast = useToast();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [catalogue, setCatalogue] = useState<SObjectSummary[] | null>(null);
  const [fields, setFields] = useState<Record<string, FieldSummary[]>>({});
  const [adding, setAdding] = useState(false);
  const [objectFilter, setObjectFilter] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');
  const [patterns, setPatterns] = useState<Pattern[]>([]);

  // What the agent is shown when it picks this skill: the name and summary it chooses on, and the
  // instruction it then reads. Edited here and saved with everything else, because they are one skill.
  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');
  const [prompt, setPrompt] = useState('');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  /**
   * Whether anything has been changed since the last load.
   *
   * The prompt and the object configuration used to have a save button each, which meant an
   * administrator could write an instruction, configure the fields, press the button nearest them and
   * lose the other half. They are one skill and they save together.
   */
  const dirty =
    skill !== null &&
    (prompt !== skill.prompt ||
      name !== skill.name ||
      summary !== skill.summary ||
      JSON.stringify(objects) !== JSON.stringify(skill.objects));


  const [schema, setSchema] = useState<SchemaStatus | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshed, setRefreshed] = useState<RefreshResult | null>(null);

  const load = useCallback(async () => {
    try {
      const detail = await api<SkillDetail>(`/api/tenants/${tenantId}/skills/${skillId}`);
      setSkill(detail);
      setObjects(detail.objects);
      setPrompt(detail.prompt);
      setName(detail.name);
      setSummary(detail.summary);
      setActive((current) => current ?? detail.objects[0]?.apiName ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that skill');
    }
  }, [tenantId, skillId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Moves a link to the old address onto the org's own.
   *
   * The sidebar shows a page's org by reading it out of the address, and the old route has none, so a
   * skill opened from a bookmark or an old ticket sat in the menu as if it belonged to no org at all.
   * Replaced rather than pushed, so Back does not land on the old address and bounce forward again. It
   * runs straight after the load, before anything can have been edited.
   */
  useEffect(() => {
    if (skill && tenantId && skillId && !routeOrgId) {
      navigate(orgPath(tenantId, skill.customerOrgId, `skills/${skillId}`), { replace: true });
    }
  }, [skill, tenantId, skillId, routeOrgId, navigate]);

  /**
   * Read-only, and allowed to fail quietly: a skill with no grants behind it yet is the normal state
   * while one is being written, and an error banner over a card that says "never" would be noise.
   */
  const loadSchema = useCallback(async () => {
    try {
      setSchema(await api<SchemaStatus>(`/api/tenants/${tenantId}/skills/${skillId}/schema`));
    } catch {
      setSchema(null);
    }
  }, [tenantId, skillId]);

  useEffect(() => {
    void loadSchema();
  }, [loadSchema]);

  useEffect(() => {
    void (async () => {
      try {
        const page = await api<{ patterns: Pattern[] }>('/api/redaction-patterns');
        setPatterns(page.patterns);
      } catch {
        setPatterns([]);
      }
    })();
  }, []);


  const current = objects.find((o) => o.apiName === active) ?? null;

  async function loadCatalogue() {
    if (!orgId) return;
    setError(null);
    setBusy(true);
    try {
      const page = await api<{ objects: SObjectSummary[] }>(`/api/tenants/${tenantId}/orgs/${orgId}/sobjects`);
      setCatalogue(page.objects);
      setAdding(true);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not read that org’s objects');
    } finally {
      setBusy(false);
    }
  }

  const loadFields = useCallback(
    async (apiName: string) => {
      if (!orgId || fields[apiName]) return;
      try {
        const page = await api<{ fields: FieldSummary[] }>(
          `/api/tenants/${tenantId}/orgs/${orgId}/sobjects/${apiName}`,
        );
        setFields((f) => ({ ...f, [apiName]: page.fields }));
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : `Could not read the fields on ${apiName}`);
      }
    },
    [tenantId, orgId, fields],
  );

  useEffect(() => {
    if (active) void loadFields(active);
  }, [active, loadFields]);

  function addObject(apiName: string) {
    if (!objects.some((o) => o.apiName === apiName)) setObjects([...objects, blankObject(apiName)]);
    setActive(apiName);
    setAdding(false);
    setObjectFilter('');
    setFieldFilter('');
  }

  function patch(apiName: string, change: Partial<ObjectConfig>) {
    setObjects((list) => list.map((o) => (o.apiName === apiName ? { ...o, ...change } : o)));
  }

  function removeObject(apiName: string) {
    const next = objects.filter((o) => o.apiName !== apiName);
    setObjects(next);
    if (active === apiName) setActive(next[0]?.apiName ?? null);
  }


  async function runPreview() {
    if (!current) return;
    setPreview(null);
    setPreviewing(true);
    try {
      setPreview(
        await api<PreviewResult>(`/api/tenants/${tenantId}/skills/${skillId}/preview`, {
          method: 'POST',
          body: { customerOrgId: orgId, object: current.apiName, limit: 10 },
        }),
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not run that skill');
    } finally {
      setPreviewing(false);
    }
  }

  async function refreshSchema() {
    setRefreshed(null);
    setRefreshing(true);
    try {
      setRefreshed(
        await api<RefreshResult>(`/api/tenants/${tenantId}/skills/${skillId}/refresh-schema`, { method: 'POST' }),
      );
      await loadSchema();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not read the schema');
    } finally {
      setRefreshing(false);
    }
  }

  /**
   * One save for the whole skill.
   *
   * The instruction and the permissions are two halves of the same thing and are written in one sitting,
   * so they are sent together. The prompt goes first: it is the cheaper call, and if the object
   * configuration is refused for being invalid the words an administrator just wrote are already safe.
   */
  async function save() {
    if (!skill) return;
    setError(null);
    setBusy(true);
    try {
      const meta: Record<string, string> = {};
      if (prompt !== skill.prompt) meta.prompt = prompt;
      if (name !== skill.name) meta.name = name.trim();
      if (summary !== skill.summary) meta.summary = summary.trim();
      if (Object.keys(meta).length) {
        await api(`/api/tenants/${tenantId}/skills/${skillId}`, { method: 'PATCH', body: meta });
      }
      await api(`/api/tenants/${tenantId}/skills/${skillId}/objects`, { method: 'PUT', body: { objects } });
      await load();
      // The stored intersection is now behind the configuration. Re-reading says so in the card rather
      // than leaving a snapshot that looks current.
      await loadSchema();
      toast.ok('Skill saved.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save this skill');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Deleting a skill, which really deletes it.
   *
   * Everything else in this console is disabled rather than removed because the audit trail points at
   * it. A skill is the exception: skill_access_event keeps the code alongside the id precisely so the
   * history still reads correctly once the skill is gone. What goes with it is every grant that carried
   * it, which is the point — a deleted skill must stop being on anybody's menu.
   */
  async function deleteSkill() {
    if (!skill) return;
    const onGrants = skill.grantCount
      ? `

It is on ${skill.grantCount} grant${skill.grantCount === 1 ? '' : 's'}, and will be taken off ${
          skill.grantCount === 1 ? 'it' : 'them'
        }.`
      : '';
    if (!window.confirm(`Delete "${skill.name}"? This cannot be undone.${onGrants}`)) return;
    setDeleting(true);
    try {
      await api(`/api/tenants/${tenantId}/skills/${skillId}`, { method: 'DELETE' });
      toast.ok(`Deleted "${skill.name}".`);
      navigate(orgPath(tenantId!, skill.customerOrgId, 'skills'));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete this skill');
      setDeleting(false);
    }
  }

  /**
   * Selected fields first, then everything else alphabetically.
   *
   * Recomputed only when the object changes rather than on every tick, so a field does not jump out from
   * under the cursor the moment it is ticked. The order settles when the tab is next opened.
   */
  const orderedFields = useMemo(() => {
    if (!current) return [];
    const all = fields[current.apiName] ?? [];
    const chosen = new Set(
      [...current.readableFields, ...current.updatableFields, ...current.creatableFields].map((f) => f.toLowerCase()),
    );
    const selected = all.filter((f) => chosen.has(f.apiName.toLowerCase()));
    const rest = all.filter((f) => !chosen.has(f.apiName.toLowerCase()));
    const byName = (a: FieldSummary, b: FieldSummary) => a.apiName.localeCompare(b.apiName);
    return [...selected.sort(byName), ...rest.sort(byName)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.apiName, fields]);

  const shownFields = orderedFields.filter((f) => {
    const needle = fieldFilter.trim().toLowerCase();
    return !needle || f.apiName.toLowerCase().includes(needle) || f.label.toLowerCase().includes(needle);
  });

  const shownObjects = (catalogue ?? []).filter((o) => {
    const needle = objectFilter.trim().toLowerCase();
    return !needle || o.apiName.toLowerCase().includes(needle) || o.label.toLowerCase().includes(needle);
  });

  if (!skill) return <p className="muted">{error ?? 'Loading…'}</p>;

  const workspaceName = me?.memberships.find((m) => m.tenantId === tenantId)?.name ?? 'Workspace';

  return (
    <div>
      {/* The header is the page's own, since the editor is too wide to sit inside the org's layout, but it
          walks the same path the menu does: workspace, then the org, then its skills. The name and summary
          are inputs in the places a heading and its subtitle would be, because they are edited where an
          agent will read them. The wrapper is only there so the name can take the whole row. */}
      <div className="skill-head">
        <PageHeader
          crumbs={[
            { label: workspaceName, to: workspacePath(tenantId!) },
            { label: skill.orgLabel, to: orgPath(tenantId!, skill.customerOrgId) },
            { label: 'Skills', to: orgPath(tenantId!, skill.customerOrgId, 'skills') },
          ]}
          title={
            <input
              className="skill-title"
              value={name}
              maxLength={120}
              aria-label="Skill name"
              onChange={(e) => setName(e.target.value)}
            />
          }
          meta={
            <>
              {/* The code is what an agent calls and what the audit log keeps, so it is not renamed here. */}
              <span className="pill">{skill.code}</span>
              <input
                className="skill-summary"
                value={summary}
                maxLength={300}
                aria-label="One-line summary"
                placeholder="One line, shown in the menu an agent chooses from"
                onChange={(e) => setSummary(e.target.value)}
              />
            </>
          }
        />
      </div>

      <div className="actions skill-topbar">
        {/* A blank name is refused here rather than on the server, where an empty string is treated as
            "unchanged" and would quietly come back as it was after an apparently successful save. */}
        <button onClick={save} disabled={busy || !dirty || !name.trim()}>
          {busy ? 'Saving…' : dirty ? 'Save skill' : 'Saved'}
        </button>
        {!name.trim() && <span className="small muted">A skill needs a name.</span>}
        {current && (
          <button className="secondary" onClick={runPreview} disabled={previewing || busy || !orgId}>
            {previewing ? 'Running…' : `Try it on ${current.apiName}`}
          </button>
        )}
        <span className="spacer" />
        <button className="danger" onClick={deleteSkill} disabled={deleting || busy}>
          {deleting ? 'Deleting…' : 'Delete skill'}
        </button>
      </div>

      {adding && (
        <div className="card">
          <h3>Add an object</h3>
          <input
            autoFocus
            value={objectFilter}
            onChange={(e) => setObjectFilter(e.target.value)}
            placeholder="Filter by name or API name"
          />
          <div className="skill-picker">
            <table>
              <thead>
                <tr>
                  <th>Object</th>
                  <th>API name</th>
                  <th>Allows</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shownObjects.slice(0, 200).map((o) => (
                  <tr key={o.apiName}>
                    <td>{o.label}</td>
                    <td className="small muted">{o.apiName}</td>
                    <td className="small muted">
                      {[o.createable && 'create', o.updateable && 'edit', o.deletable && 'delete']
                        .filter(Boolean)
                        .join(', ') || 'read only'}
                    </td>
                    <td>
                      <button
                        className="secondary"
                        disabled={objects.some((c) => c.apiName === o.apiName)}
                        onClick={() => addObject(o.apiName)}
                      >
                        {objects.some((c) => c.apiName === o.apiName) ? 'Added' : 'Add'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {shownObjects.length > 200 && (
              <p className="small muted">Showing the first 200. Keep typing to narrow it down.</p>
            )}
          </div>
          <div className="actions">
            <button className="secondary" onClick={() => setAdding(false)}>
              Done
            </button>
          </div>
        </div>
      )}

      <div className="skill-layout">
        <div className="skill-col">
          <div className="card">
            <h3>How to do this task</h3>
            <p className="small muted">
              The agent reads this when it picks the skill. Write it as instructions to somebody competent who
              has never seen this org: which records matter, what “done” looks like, and what not to touch.
              The objects and fields below are what it <em>may</em> do; this is what you want it to do.
            </p>
            <textarea
              rows={6}
              value={prompt}
              placeholder={
                'Find the open cases this person owns that have had no reply in three days. Summarise each in ' +
                'one line with the account name. Do not change anything.'
              }
              onChange={(e) => {
                setPrompt(e.target.value);
              }}
            />
          </div>

          <div className="card">
            <h3>What this skill can actually reach</h3>
            <p className="small muted">
              The fields you select are what this skill is restricted to.
              It is also filtered against the user's own permissions.
            </p>

            {!schema || !schema.identities.length ? (
              <p className="notice">
                {schema?.blockedBecause?.length ? (
                  <>
                    There are grants carrying this skill, but none can be used to read the org:{' '}
                    {schema.blockedBecause.join(' ')}
                  </>
                ) : (
                  <>
                    No live grant carries this skill yet, so there is no identity to read the org as. Add
                    one on the org’s{' '}
                    <Link to={orgPath(tenantId!, skill.customerOrgId, 'grants')}>Access grants</Link> page with
                    Omni MCP ticked, then come back.
                  </>
                )}
              </p>
            ) : (
              <table className="small">
                <thead>
                  <tr>
                    <th>Identity</th>
                    <th>Last read</th>
                    <th>Objects</th>
                    <th>Fields</th>
                  </tr>
                </thead>
                <tbody>
                  {schema.identities.map((i) => (
                    <tr key={i.identityId}>
                      <td>{i.username}</td>
                      <td>
                        {i.capturedAt ? (
                          new Date(i.capturedAt).toLocaleString()
                        ) : (
                          <span className="muted">never</span>
                        )}
                      </td>
                      <td>{i.capturedAt ? i.objectCount : '—'}</td>
                      <td>{i.capturedAt ? i.fieldCount : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {schema && schema.orphaned.length > 0 && (
              <p className="notice">
                {schema.orphaned.length} stored {schema.orphaned.length === 1 ? 'reading describes' : 'readings describe'}{' '}
                an identity no grant points at any more, which is what repointing a grant leaves behind. A
                refresh replaces what is still in use; the rest is ignored.
              </p>
            )}

            <div className="actions">
              <button
                className="secondary"
                onClick={refreshSchema}
                disabled={refreshing || busy || !schema?.identities.length}
              >
                {refreshing ? 'Reading the org…' : 'Refresh metadata'}
              </button>
            </div>


            {refreshed && (
              <>
                <p className="small">
                  {refreshed.captured} of {refreshed.results.length}{' '}
                  {refreshed.results.length === 1 ? 'identity' : 'identities'} read
                  {refreshed.failed > 0 && ` · ${refreshed.failed} failed`}
                </p>
                {refreshed.results.map((r) => (
                  <div key={r.identityId} className="small">
                    <strong>{r.username}</strong>
                    {r.ok ? (
                      <span>
                        {' '}
                        — {r.objectCount} {r.objectCount === 1 ? 'object' : 'objects'}, {r.fieldCount}{' '}
                        {r.fieldCount === 1 ? 'field' : 'fields'}
                      </span>
                    ) : (
                      <span className="alert"> — {r.error}</span>
                    )}
                    {r.warnings && r.warnings.length > 0 && (
                      <ul className="muted">
                        {/* Said rather than swallowed: a field the configuration names but the identity
                            cannot reach is dropped, and an admin who is not told will assume it is in play. */}
                        {r.warnings.map((w, n) => (
                          <li key={n}>
                            {w.objectName}
                            {w.fieldName ? `.${w.fieldName}` : ''} — {w.reason}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>

          {preview && (
            <div className="card">
              <h3>What an agent would get back</h3>
              <p className="small muted">
                This ran the skill for real, as you, against {skill.orgLabel}
                {' '}— the same checks, the same filters, the same redaction, and a row in the skill log. Save
                the skill first if you have just changed it.
              </p>
              {preview && (
                <>
                  <p className="small">
                    {preview.count} {preview.count === 1 ? 'record' : 'records'}
                    {preview.partial && ' (not all of them)'}
                    {preview.redactions > 0 && ` · ${preview.redactions} redacted`}
                  </p>
                  {preview.count === 0 ? (
                    <p className="notice">
                      Nothing came back. Either no record matches, or the read filter excluded them all — which
                      is worth knowing now rather than from an agent reporting that the org is empty.
                    </p>
                  ) : (
                    <div className="skill-preview">
                      <table>
                        <thead>
                          <tr>
                            {preview.fields.map((f) => (
                              <th key={f}>{f}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {preview.records.map((r, i) => (
                            <tr key={String(r.Id ?? i)}>
                              {preview.fields.map((f) => {
                                const value = r[f] === null || r[f] === undefined ? '—' : String(r[f]);
                                // The whole value on hover, since the cell truncates.
                                return (
                                  <td key={f} title={value}>
                                    {value}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="skill-col">
          <div className="card">
            <div className="row">
              <button className="secondary" onClick={loadCatalogue} disabled={busy || !orgId}>
                Add an object
              </button>
            </div>
            <p className="small muted">
              Objects and fields come from <strong>{skill.orgLabel}</strong>, the org this skill belongs to,
              and are read as the Salesforce identity a skill here runs as rather than as you. So this list is
              what that identity can genuinely reach: anything it cannot see is not offered, because a skill
              built around it would fail on every call.
            </p>
          </div>

          <div className="skill-objects">
          {objects.length > 0 && (
            <nav className="vtabs">
              {objects.map((o) => (
                <a
                  key={o.apiName}
                  href="#object"
                  className={o.apiName === active ? 'active' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    setActive(o.apiName);
                    setFieldFilter('');
                  }}
                >
                  {o.apiName}
                </a>
              ))}
            </nav>
          )}

          {current && (
            <div className="card" id="object">
              <div className="panel-head">
                <h3>{current.apiName}</h3>
                {/* An icon, and off to the side: removing an object is rare and destructive, and it was
                    sitting next to the name at the size of the primary action. */}
                <button
                  type="button"
                  className="iconbutton danger"
                  title={`Remove ${current.apiName} from this skill`}
                  aria-label={`Remove ${current.apiName} from this skill`}
                  onClick={() => removeObject(current.apiName)}
                >
                  🗑
                </button>
              </div>

              <input
                value={fieldFilter}
                onChange={(e) => setFieldFilter(e.target.value)}
                placeholder="Filter fields"
              />

              {!fields[current.apiName] && <p className="muted">Reading the fields…</p>}

              {fields[current.apiName] && (
                <div className="skill-fields">
                  <table>
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th title="The agent may read this field">Read</th>
                        <th title="Scan this field and replace card numbers, NI numbers and the like before the agent sees it. Offered only where the field cannot be filtered, sorted or grouped.">
                          PII
                        </th>
                        <th title="The agent may change this field on an existing record">Edit</th>
                        <th title="The agent may set this field on a new record">Create</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shownFields.map((f) => {
                        const readable = has(current.readableFields, f.apiName);
                        return (
                          <tr key={f.apiName} className={readable ? 'chosen' : undefined}>
                            <td>
                              {f.label}
                              <div className="small muted">
                                {f.apiName} · {f.type}
                                {f.redactable && <span className="pill">redactable</span>}
                              </div>
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={readable}
                                onChange={() => {
                                  const readableFields = toggled(current.readableFields, f.apiName);
                                  // A field that stops being readable cannot stay marked as personal data:
                                  // the marking would describe something the skill can no longer see.
                                  patch(current.apiName, {
                                    readableFields,
                                    piiFields: has(readableFields, f.apiName)
                                      ? current.piiFields
                                      : current.piiFields.filter((p) => p.toLowerCase() !== f.apiName.toLowerCase()),
                                  });
                                }}
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={has(current.piiFields, f.apiName)}
                                disabled={!readable || !f.redactable}
                                title={
                                  !f.redactable
                                    ? 'Only fields that cannot be filtered, sorted or grouped can be redacted — ' +
                                      'long text areas, and anything Shield or Classic encryption has taken ' +
                                      'those away from. A field that can still be matched could be probed a ' +
                                      'comparison at a time, so redacting it would hide the value without ' +
                                      'protecting it.'
                                    : readable
                                      ? undefined
                                      : 'Mark it readable first'
                                }
                                onChange={() => patch(current.apiName, { piiFields: toggled(current.piiFields, f.apiName) })}
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={has(current.updatableFields, f.apiName)}
                                disabled={!f.updateable}
                                title={f.updateable ? undefined : 'Salesforce does not allow this field to be changed'}
                                onChange={() =>
                                  patch(current.apiName, { updatableFields: toggled(current.updatableFields, f.apiName) })
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={has(current.creatableFields, f.apiName)}
                                disabled={!f.createable}
                                title={f.createable ? undefined : 'Salesforce does not allow this field to be set on create'}
                                onChange={() =>
                                  patch(current.apiName, { creatableFields: toggled(current.creatableFields, f.apiName) })
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <label className="channel">
                <input
                  type="checkbox"
                  checked={current.deletable}
                  onChange={() =>
                    patch(current.apiName, {
                      deletable: !current.deletable,
                      deleteFilter: current.deletable ? null : current.deleteFilter,
                    })
                  }
                />
                <span>
                  <strong>Allow delete</strong>
                  <span className="small muted"> — the agent may delete records of this object.</span>
                </span>
              </label>

              <h4>Attachments</h4>
              <label className="channel">
                <input
                  type="checkbox"
                  checked={current.filesReadable}
                  onChange={() => patch(current.apiName, { filesReadable: !current.filesReadable })}
                />
                <span>
                  <strong>Read files attached to these records</strong>
                  <span className="small muted">
                    {' '}
                    — the agent can list what is attached and open one. The read filter above decides whose
                    files it can reach, and the person’s own Salesforce sharing applies on top. What actually
                    arrives depends on the agent: today PDFs, plain text and JPEG, PNG, GIF and WebP images
                    travel, and a Word or Excel file has no way to reach one. That is the agent’s limit rather
                    than a setting here, and it will move as agents improve.
                  </span>
                </span>
              </label>
              <label className="channel">
                <input
                  type="checkbox"
                  checked={current.filesWritable}
                  onChange={() => patch(current.apiName, { filesWritable: !current.filesWritable })}
                />
                <span>
                  <strong>Attach files to these records</strong>
                  <span className="small muted">
                    {' '}
                    — the agent can add a file. Anyone who can see the record can see what it attached, and it
                    cannot be removed from here.
                  </span>
                </span>
              </label>

              {(current.filesReadable || current.filesWritable) && (
                <>
                  {/* One sentence, stated where the decision is actually made. */}
                  <p className="alert">
                    <strong>Files are not redacted.</strong> A card number in a text field is found and
                    replaced every time. A file is passed to the agent exactly as it is stored — nothing opens
                    it on the way past, so whatever is in it, the agent sees. The skill log records which files
                    were read, by whom and when, so it can be shown afterwards. If your DPIA cannot carry that,
                    leave this off.
                  </p>
                  <label>
                    Largest file
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={Math.round(current.fileMaxBytes / 1_048_576)}
                      onChange={(e) =>
                        patch(current.apiName, {
                          fileMaxBytes: Math.min(100, Math.max(1, Number(e.target.value) || 1)) * 1_048_576,
                        })
                      }
                    />
                    <span className="small muted">
                      In megabytes, each way. Anything bigger is refused rather than trimmed, so the agent is
                      never handed part of a document believing it has the whole. This is a safety limit, not
                      a target: the real ceiling is the agent’s context, and a PDF is charged by the page, so
                      a document well inside this can still be too large for the agent to take.
                    </span>
                  </label>
                </>
              )}

              {current.piiFields.length > 0 && (
                <>
                  <h4>What to redact</h4>
                  <p className="small muted">
                    {current.piiFields.join(', ')} {current.piiFields.length === 1 ? 'is' : 'are'} scanned before
                    the agent sees {current.piiFields.length === 1 ? 'it' : 'them'}. Matches are replaced with
                    [REDACTED-…] and cannot be recovered. Leave every box ticked unless you have a reason.
                  </p>
                  <div className="channel-list">
                    {patterns.map((p) => {
                      // Empty means all of them, so an untouched configuration looks the way it behaves.
                      const on = !current.piiPatterns.length || current.piiPatterns.includes(p.name);
                      return (
                        <label key={p.name} className="channel">
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => {
                              const currently = current.piiPatterns.length
                                ? current.piiPatterns
                                : patterns.map((q) => q.name);
                              const next = on ? currently.filter((n) => n !== p.name) : [...currently, p.name];
                              patch(current.apiName, {
                                piiPatterns: next.length === patterns.length ? [] : next,
                              });
                            }}
                          />
                          <span>
                            <strong>{p.label}</strong>
                            <span className="small muted"> — {p.description}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}

              <h4>Which records</h4>
              <p className="small muted">
                SOQL added to every statement for this object. Leave one empty to put no limit of your own on
                it — the person’s own Salesforce permissions still apply either way. Merge fields: {MERGE_HINT}
              </p>
              {(
                [
                  ['readFilter', 'Read filter', 'Every query, and every write is checked against this too'],
                  ['updateFilter', 'Edit filter', 'Narrows which records may be changed'],
                  ['deleteFilter', 'Delete filter', 'Narrows which records may be deleted'],
                ] as const
              ).map(([key, label, hint]) => (
                <label key={key}>
                  {label}
                  <input
                    value={current[key] ?? ''}
                    disabled={key === 'deleteFilter' && !current.deletable}
                    placeholder={key === 'readFilter' ? 'OwnerId = {!$User.Id}' : ''}
                    onChange={(e) => patch(current.apiName, { [key]: e.target.value.trim() ? e.target.value : null })}
                  />
                  <span className="small muted">{hint}</span>
                </label>
              ))}

              <label>
                Most records in one call
                <input
                  type="number"
                  min={1}
                  max={2000}
                  value={current.maxRows}
                  onChange={(e) => patch(current.apiName, { maxRows: Number(e.target.value) })}
                />
                <span className="small muted">
                  A ceiling on what one call returns, so a large limit cannot turn this into an export.
                </span>
              </label>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
